import React from "react";
import { DateUtility } from "./dateUtility";
import NextImage from "../components/common/NextImage"
import { getImgUrl } from "./constants";

export const MinutesCalculate = (
  statusName,
  statusUpdatedDateTime,
  interruptedMinutes,
  minutes
) => {
  const s = statusName?.toLowerCase();

  // Handle no status
  if (!s) return minutes === 'Finished' ? 'FT' : minutes || '';

  // Fix: compare against lowercased value
  if (s === 'half time') return 'HT';

  // Parse time robustly (avoid fragile string concat of offsets)
  const statusTime = safeParseDate(statusUpdatedDateTime);
  if (!statusTime) {
    // Fallback to given minutes when we can't parse a time
    const num = Number(minutes);
    if (!Number.isNaN(num) && num > 0) return num;
    if (typeof minutes === 'string') {
      if (minutes === 'Finished') return 'FT';
      if (minutes.includes('+')) return minutes;
      return minutes;
    }
    return '';
  }

  const diffMs = Date.now() - statusTime.getTime();

  // If status time is in the future, trust provided minutes (e.g., pre-kickoff or delayed)
  if (diffMs < 0) {
    const num = Number(minutes);
    return !Number.isNaN(num) ? num : (minutes?.includes('+') ? minutes : '');
  }

  const extra = Number.parseInt(interruptedMinutes ?? 0, 10) || 0;

  // Correct football minute math: floor + 1
  const elapsedWhole = Math.floor(diffMs / 60000) + extra;
  const tick = elapsedWhole + 1; // 0–59s -> 1'

  // Map by period with correct bases & caps
  if (s === '1st half') {
    return tick > 45 ? '45+' : tick;
  }
  if (s === '2nd half') {
    const m = 45 + tick;      // 0' of 2H -> 46'
    return m > 90 ? '90+' : m;
  }
  if (s === 'et 1st half') {
    const m = 90 + tick;      // 0' of ET1 -> 91'
    return m > 105 ? '105+' : m;
  }
  if (s === 'et 2nd half') {
    const m = 105 + tick;     // 0' of ET2 -> 106'
    return m > 120 ? '120+' : m;
  }
  if (s === 'penalty') return '120+';

  // Unknown/other statuses
  return minutes || 'FT';
};

// Tries hard to parse common backend formats safely.
function safeParseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value === 'number') return new Date(value);

  if (typeof value === 'string') {
    // If already ISO-ish or has TZ, let Date handle it.
    const hasTZ = /[+-]\d\d:?\d\d$/.test(value) || value.endsWith('Z');
    const hasT = value.includes('T');

    if (hasTZ || hasT) {
      const d1 = new Date(value);
      if (!isNaN(d1)) return d1;
    }

    // Treat "YYYY-MM-DD HH:mm:ss" as UTC
    const isoGuess = value.replace(' ', 'T') + 'Z';
    const d2 = new Date(isoGuess);
    return isNaN(d2) ? null : d2;
  }

  return null;
}


export function linkName(data) {
  if (!data) return "";
  return titleToSlug(data?.toLowerCase().replace(/[\s/]+/g, "-")) || "";
}

export const capitalized1LetterS = (string) => {
  if (!string) return;
  string = string?.replaceAll(/[_-]/g, " ");
  return string?.trim()?.replace(/\b\w/g, function (l) {
    return l.toLocaleUpperCase();
  });
};

export const calculatePercentage = (numerator, denominator) => {
  if (numerator === 0) return 0;
  const percentage = (numerator / denominator) * 100;
  if (percentage) return percentage || 0;
};

export const uniqueId = (val = 1) => {
  val = val + 1;
  let id = Date.now();
  id = id * val;
  return id;
};

export const timeSince = (date, dictionary) => {
  const currentTime = new Date();
  const inputTime = new Date(date);
  const timeDiff = currentTime - inputTime;

  const units = [
    {
      label: `${dictionary?.year || "year"}`,
      value: 1000 * 60 * 60 * 24 * 365,
    },
    {
      label: `${dictionary?.month || "month"}`,
      value: 1000 * 60 * 60 * 24 * 30,
    },
    { label: `${dictionary?.day || "day"}`, value: 1000 * 60 * 60 * 24 },
    { label: `${dictionary?.hour || "hour"}`, value: 1000 * 60 * 60 },
    { label: `${dictionary?.minute || "minute"}`, value: 1000 * 60 },
  ];

  for (const unit of units) {
    const count = Math.floor(timeDiff / unit.value);
    if (count > 0) {
      return (
        count +
        " " +
        unit.label +
        (count === 1
          ? ` ${dictionary?.ago || "ago"}`
          : `s ${dictionary?.ago || "ago"}`)
      );
    }
  }

  return "just now";
};

export const calculatePercentagestats = (value1, value2) => {
  if (value1 === 0) return 0;
  const value = (value1 * 100) / (value1 + value2);
  if (value) return value;
};

export const scoreSplitter = (val, ind, splitBy = "-") => {
  const text = val?.split(splitBy);
  return text[ind] || "";
};

export const CompareObjectsByDatetime = (a, b) => {
  const dateA = new Date(a.datetime);
  const dateB = new Date(b.datetime);
  return dateA - dateB;
};

export const eventClickerChildToParent = (messageData) => {
  const origin = window.location.origin;
  window.opener.postMessage(messageData, origin);
};

export const sportNameFromPathName = (pathname) => {
  let sport = "football";
  if (pathname.includes("tennis")) {
    sport = "tennis";
  } else if (pathname.includes("cricket")) {
    sport = "cricket";
  } else if (pathname.includes("basketball")) {
    sport = "basketball";
  } else if (pathname.includes("rugby-union")) {
    sport = "rugby-union";
  } else if (pathname.includes("rugby-league")) {
    sport = "rugby-league";
  }
  return sport;
};

export const firstTenMatches = (data, count = 10) => {
  let totalMatchCount = 0;
  const newTournaments = [];

  for (const tournament of data) {
    if (totalMatchCount >= count) {
      break; // Stop if the total match count reaches count
    }

    const remainingMatches = count - totalMatchCount;
    const matchesToAdd = Math.min(remainingMatches, tournament.matches.length);

    newTournaments.push({
      ...tournament,
      matches: tournament.matches.slice(0, matchesToAdd),
    });
    totalMatchCount += matchesToAdd;
  }
  return newTournaments || [];
};

export const firstCountMatches = (data, sport = "football", count = 15) => {
  let totalMatchCount = 0;
  const newArrayOfObjects = [];
  for (const obj of data) {
    if (totalMatchCount >= count) {
      break;
    }

    const matchesToAdd = Math?.min(count - totalMatchCount, obj?.matches.length);

    newArrayOfObjects.push({
      ...obj,
      matches: obj.matches.slice(0, matchesToAdd),
    });

    totalMatchCount += matchesToAdd;
  }
  let retObj;
  if (sport === "football" || sport === "rugby") {
    retObj = {
      data: newArrayOfObjects || [],
    };
  } else {
    retObj = newArrayOfObjects || [];
  }

  return retObj;
};

export const matchFormClass = (text) => {
  let clas = "";
  if (text === "W") {
    clas = "draw_win";
  } else if (text === "L") {
    clas = "draw_loss";
  } else if (text === "D") {
    clas = "draw_mos";
  }
  return clas;
};

export function updateCountryInURL(url, changeKey, changeValue) {
  // Split the URL into base and parameters
  var urlParts = url.split("?");
  var baseUrl = urlParts[0];
  var params = urlParts[1];

  // Split the parameters into key-value pairs
  var paramList = params.split("&");
  var paramDict = {};

  // Populate the parameters into a dictionary
  paramList.forEach(function (param) {
    var keyValue = param.split("=");
    paramDict[keyValue[0]] = keyValue[1];
  });

  // Update the country parameter value
  paramDict[changeKey] = changeValue;

  // Construct the updated URL
  var updatedParams = Object.keys(paramDict)
    .map(function (key) {
      return key + "=" + paramDict[key];
    })
    .join("&");

  var updatedURL = baseUrl + "?" + updatedParams;
  return updatedURL;
}

export const titleToSlug = (text) => {
  if (!text) return "";

  const cleanedText = text
    .replace(/["',]/g, "")     // remove quotes and commas
    .replace(/\//g, "-")       // replace slashes with dashes
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");     // replace spaces with dashes

  return encodeURIComponent(cleanedText);
};

export function getNextSevenDays(dictionary) {
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // const monthsOfYear = [
  //   "Jan",
  //   "Feb",
  //   "Mar",
  //   "Apr",
  //   "May",
  //   "Jun",
  //   "Jul",
  //   "Aug",
  //   "Sep",
  //   "Oct",
  //   "Nov",
  //   "Dec",
  // ];
  const today = new Date(); // Get today's date

  const sevenDays = [];
  for (let i = -3; i <= 3; i++) {
    const date = new Date(today); // Create a new date object for each day
    date.setDate(today.getDate() + i); // Increment the date by i days
    const dayOfWeek = daysOfWeek[date.getDay()]; // Get the day of the week
    const dayOfMonth = date.getDate(); // Get the day of the month
    // const month = monthsOfYear[date.getMonth()]; // Get the month abbreviation

    let formattedDay;
    if (i === 0) {
      formattedDay = "Today";
    } else {
      formattedDay = dayOfWeek;
    }

    const slugDate = date.toISOString().slice(0, 10); // Get the slug date format

    const formattedDate = {
      day: formattedDay,
      date: `${dayOfMonth}`,
      slugDate: slugDate,
    }; // Format the date as an object

    sevenDays.push(formattedDate); // Add the formatted date object to the array
  }

  return sevenDays;
}

export function getCurrentDate() {
  const today = new Date(); // Get today's date
  const year = today.getFullYear(); // Get the year
  let month = today.getMonth() + 1; // Get the month (0-indexed)
  let day = today.getDate(); // Get the day of the month

  // Add leading zeros if month or day is a single digit
  month = month < 10 ? "0" + month : month;
  day = day < 10 ? "0" + day : day;

  return `${year}-${month}-${day}`;
}

export function processMyArr(MyArr) {
  MyArr = MyArr?.map((val) => val?.toLowerCase());
  const hasSocial = MyArr?.includes("social");
  const hasNews = MyArr?.includes("News");
  const hasEvents = MyArr?.includes("events");
  const hasInfo = MyArr?.includes("info");
  if (hasSocial && hasNews) {
    MyArr = MyArr?.filter((item) => item !== "social");
  } else if (hasSocial) {
    MyArr = MyArr?.map((item) => (item === "social" ? "news" : item));
  } else if (hasEvents) {
    MyArr = MyArr.filter((item) => item !== "events");
  } else if (hasInfo) {
    MyArr = MyArr?.filter((item) => item !== "info");
  }
  MyArr?.unshift("info");
  const filtered = Array.isArray(MyArr)
    ? MyArr.filter((item) => item !== "summary")
    : [];
  return filtered;
}

export function processRugbyTabs(arr) {
  const array = processMyArr(arr);
  const filtered = Array.isArray(array)
    ? array.filter((item) => item !== "summary")
    : [];

  return filtered;
}
export const GetMatchScores = (scores, status) => {
  let obj = {};

  let s1 =
    status === "1st set"
      ? scores?.find((x) => ToLowerText(x?.ty) === "cs")
      : scores?.find((x) => ToLowerText(x?.ty) === "1set");
  let s2 =
    status === "2nd set"
      ? scores?.find((x) => ToLowerText(x?.ty) === "cs")
      : scores?.find((x) => ToLowerText(x?.ty) === "2set");
  let s3 =
    status === "3rd set"
      ? scores?.find((x) => ToLowerText(x?.ty) === "cs")
      : scores?.find((x) => ToLowerText(x?.ty) === "3set");
  let s4 =
    status === "4th set"
      ? scores?.find((x) => ToLowerText(x?.ty) === "cs")
      : scores?.find((x) => ToLowerText(x?.ty) === "4set");
  let s5 =
    status === "5th set"
      ? scores?.find((x) => ToLowerText(x?.ty) === "cs")
      : scores?.find((x) => ToLowerText(x?.ty) === "5set");
  let game = scores?.find((x) => ToLowerText(x?.ty) === "cp");

  obj.s1home = s1?.nm?.split(":")?.[0]?.trim();
  obj.s1away = s1?.nm?.split(":")?.[1]?.trim();
  obj.s1hometb = s1?.tb?.split(":")?.[0]?.trim();
  obj.s1awaytb = s1?.tb?.split(":")?.[1]?.trim();

  obj.s2home = s2?.nm?.split(":")?.[0]?.trim();
  obj.s2away = s2?.nm?.split(":")?.[1]?.trim();
  obj.s2hometb = s2?.tb?.split(":")?.[0]?.trim();
  obj.s2awaytb = s2?.tb?.split(":")?.[1]?.trim();

  obj.s3home = s3?.nm?.split(":")?.[0]?.trim();
  obj.s3away = s3?.nm?.split(":")?.[1]?.trim();
  obj.s3hometb = s3?.tb?.split(":")?.[0]?.trim();
  obj.s3awaytb = s3?.tb?.split(":")?.[1]?.trim();

  obj.s4home = s4?.nm?.split(":")?.[0]?.trim();
  obj.s4away = s4?.nm?.split(":")?.[1]?.trim();
  obj.s4hometb = s4?.tb?.split(":")?.[0]?.trim();
  obj.s4awaytb = s4?.tb?.split(":")?.[1]?.trim();

  obj.s5home = s5?.nm?.split(":")?.[0]?.trim();
  obj.s5away = s5?.nm?.split(":")?.[1]?.trim();
  obj.s5hometb = s5?.tb?.split(":")?.[0]?.trim();
  obj.s5awaytb = s5?.tb?.split(":")?.[1]?.trim();

  obj.gamehome =
    status !== "finished" ? game?.nm?.split(":")?.[0]?.trim() : null;
  obj.gameaway =
    status !== "finished" ? game?.nm?.split(":")?.[1]?.trim() : null;

  return obj;
};

export const ToLowerText = (text) => {
  try {
    return text?.toLowerCase();
  } catch (error) {
    console.error("Error converting text to lowercase:", error);
  }
};
export const GetTennisMatchStatus = (status, currentperiod) => {
  const normalizedStatus = ToLowerText(status);
  const normalizedPeriod = ToLowerText(currentperiod);

  const validPeriods = ["1st set", "2nd set", "3rd set", "4th set", "5th set"];

  const statusMap = {
    nsy: "nsy",
    "1st set": "1st set",
    "2nd set": "2nd set",
    "3rd set": "3rd set",
    "4th set": "4th set",
    "5th set": "5th set",
    finished: "finished",
  };

  if (statusMap[normalizedStatus]) {
    return statusMap[normalizedStatus];
  }

  if (
    normalizedStatus === "inprogress" &&
    validPeriods.includes(normalizedPeriod)
  ) {
    return normalizedPeriod;
  }

  return "unknown";
};

export function CricketScore(input) {
  const parts = input.split("RR");
  return parts[0].trim();
}

export function parseScoreData(data) {
  data = CricketScore(data);
  const [scores, overs] = data.split("(");
  const overValue = overs.replace(" Over)", "").trim();
  return {
    scores: scores.trim(),
    overs: overValue,
  };
}
export const renderBasketballScores = (scores, winner, live, cfs, fs) => {
  // Split cfs into home and away scores
  const [homeCfs, awayCfs] = cfs ? cfs.split("-") : ["", ""];
  const [totalHomeScore, totalAwayScore] = fs ? fs.split("-") : ["", ""];
  return (
    <>
      {live ? (
        <>
          <div className="game_score">
            <span className={`score bold`}>
              {homeCfs}
            </span>
            <span className={`score bold`}>
              {awayCfs}
            </span>
          </div>
          {scores
            ?.filter((score) => score.hScore && score.aScore)
            ?.map((score, index) => (
              <div key={index + 1} className="set_score_wrp">
                <span className="score">{score.hScore}</span>
                <span className="score">{score.aScore}</span>
              </div>
            ))}
        </>
      ) : (
        <>
          <div className="game_score">
            <span
              className={`score bold`}
            >
              {totalHomeScore}
            </span>
            <span
              className={`score bold`}
            >
              {totalAwayScore}
            </span>
          </div>

          {scores
            ?.filter((score) => score.hScore && score.aScore)
            ?.map((score, index) => (
              <div key={index + 1} className="set_score_wrp">
                <span className="score">{score.hScore}</span>
                <span className="score">{score.aScore}</span>
              </div>
            ))}
        </>
      )}
    </>
  );
};


export const renderTennisScores = (matchScore, hscore, ascore, live) => (
  <>

    <div className="game_score">
      <span className={`score ${hscore > ascore ? "bold" : ""}`}>
        {hscore}
        <sup></sup>
      </span>
      <span className={`score ${hscore < ascore ? "bold" : ""}`}>
        {ascore}
        <sup></sup>
      </span>
    </div>
    <div className="set_score_wrp">
      <div className="set_score">
        {["s1home", "s2home", "s3home", "s4home", "s5home"].map(
          (key, index) =>
            matchScore[key] && (
              <span key={index + 1} className="score">
                {matchScore[key]}
                <sup>{matchScore[`s${index + 1}hometb`]}</sup>
              </span>
            )
        )}
      </div>
      <div className="set_score">
        {["s1away", "s2away", "s3away", "s4away", "s5away"].map(
          (key, index) =>
            matchScore[key] && (
              <span key={index + 1} className="score">
                {matchScore[key]}
                <sup>{matchScore[`s${index + 1}awaytb`]}</sup>
              </span>
            )
        )}
      </div>
    </div>
    {live &&
      matchScore?.gamehome !== undefined &&
      matchScore?.gameaway !== undefined && (
        <div className="game_score">
          <span className="score">
            {matchScore.gamehome}
            <sup></sup>
          </span>
          <span className="score">
            {matchScore.gameaway}
            <sup></sup>
          </span>
        </div>
      )}
  </>
);

export const renderCricketScores = (match) => (
  <div className="game_score">
    <span className={`${match?.win === 1 ? "bold" : ""}`}>
      {CricketScore(match?.hts) || "-"}
    </span>
    <span className={`${match?.win === 2 ? "bold" : ""}`}>
      {CricketScore(match?.ats) || "-"}
    </span>
  </div>
);

export const getMatchInfo = (match, live) => {
  const { st, dt, ldt, bs, mins } = match || {};

  let statusText = "";
  if (st === "Finished") {
    statusText = mins === "To Finish" ? "To FT" : "Finished";
  } else if (st === "Interrupted") {
    statusText = "Interrupted";
  } else if (st === "Innings break") {
    statusText = "Innings break";
  } else if (st === "Full Time") {
    statusText = "Finished";
  } else if (st === "NSY" || st === "Not Started" || st === "Finished AET" || st === "Finished AP") {
    statusText = st;
  }


  const statusClass = ["Finished", "Interrupted", "Full Time"].includes(st)
    ? "status red"
    : "status";

  const liveStatusText = live
    ? "Live"
    : bs !== "Delayed" && bs !== "Stumps" && bs !== "Cancelled"
      ? DateUtility?.FormatDateh2h(new Date(dt || ldt))
      : "";

  return { statusText, statusClass, liveStatusText };
};

export function removeSubstring(str, substring) {
  return str.replace(substring, "");
}

export function convertDateTime(dateTimeStr) {
  const date = new Date(dateTimeStr?.replace(" ", "T"));
  const options = {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  const datePart = date?.toLocaleDateString("en-GB", options);
  return datePart;
}

//golf score css
export function getScoreColor(pts, par) {
  const score = pts - par;

  if (score === -3) {
    return "double-eagle"; // Double Eagle
  } else if (score === -2) {
    return "eagle"; // Eagle
  } else if (score === -1) {
    return "birdie"; // Birdie
  } else if (score === 0) {
    return "par"; // Par
  } else if (score === 1) {
    return "bogey"; // Bogey
  } else if (score === 2) {
    return "dou_bogey"; // Double Bogey
  } else {
    return "three_bagey"; // Triple Bogey or worse
  }
}

export function getUtcOffset(timezone) {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "short",
  });
  const parts = formatter?.formatToParts(now);

  const timeZonePart = parts?.find((part) => part?.type === "timeZoneName");
  const offsetString = timeZonePart ? timeZonePart?.value : "UTC";
  return extractGmtOffset(offsetString);
}

function extractGmtOffset(gmtString) {
  const match = gmtString?.match(/GMT([+-]\d+)/);
  return match ? match?.[1] : 0;
}

//Return if a team Wins / loses / match is Draw
export function getMatchOutcome(team_id, match) {
  const teamId = parseInt(team_id);
  const homeTeam = match?.competitors?.htid;
  const awayTeam = match?.competitors?.atid;
  const finalScore = match?.ft.split("-")?.map(Number);
  const penaltyScore = match?.ps ? match?.ps?.split("-")?.map(Number) : null;

  let teamScore, opponentScore;

  // Determine if the team is playing home or away
  if (teamId === homeTeam) {
    teamScore = finalScore[0];
    opponentScore = finalScore[1];
  } else if (teamId === awayTeam) {
    teamScore = finalScore[1];
    opponentScore = finalScore[0];
  } else {
    throw new Error("Team not found in match");
  }

  // Determine result
  if (teamScore > opponentScore) {
    return "W"; // Win
  } else if (teamScore < opponentScore) {
    return "L"; // Loss
  } else {
    // If it was a draw after regular time, check penalty shootout if it exists
    if (penaltyScore) {
      const teamPenaltyScore =
        teamId === homeTeam ? penaltyScore[0] : penaltyScore[1];
      const opponentPenaltyScore =
        teamId === homeTeam ? penaltyScore[1] : penaltyScore[0];
      return teamPenaltyScore > opponentPenaltyScore ? "W" : "L";
    }
    return "D"; // Draw
  }
}
export function convertDate(dateTimeString) {
  // Create a new Date object from the input string
  const date = new Date(dateTimeString);

  // Extract the year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");

  // Format the date as YYYY-MM-DD
  return `${year}-${month}-${day}`;
}

export function LinkMaker(sport, href = "", type) {
  let response = href;

  if (type === "link") {
    response = href?.replace(sport, getUrlSport(sport));
  } else if (type === "sport") {
    response = mapSport(sport);
  }
  return response;
}

export const getUrlSport = (sport) => {
  switch (sport) {
    case "football":
      return process.env.NEXT_PUBLIC_FOOTBALL || sport;
    case "rugby":
      return process.env.NEXT_PUBLIC_RUGBY || sport;
    case "tennis":
      return process.env.NEXT_PUBLIC_TENNIS || sport;
    case "basketball":
      return process.env.NEXT_PUBLIC_BASKETBALL || sport;
    case "cricket":
      return process.env.NEXT_PUBLIC_CRICKET || sport;
    default:
      return sport;
  }
};

export const mapSport = (sport) => {
  const sportEnvMap = {
    football: process.env.NEXT_PUBLIC_FOOTBALL,
    rugby: process.env.NEXT_PUBLIC_RUGBY,
    tennis: process.env.NEXT_PUBLIC_TENNIS,
    basketball: process.env.NEXT_PUBLIC_BASKETBALL,
    cricket: process.env.NEXT_PUBLIC_CRICKET,
  };

  // Reverse mapping: Find the key if the value matches the input sport
  const reverseMap = Object.entries(sportEnvMap).find(
    ([key, value]) => value === sport
  );

  // Return the original key or the original value
  return reverseMap
    ? reverseMap[0]
    : Object.keys(sportEnvMap).find((key) => sport === key);
};

export const pages = (page) => {
  switch (page) {
    case "contact-us":
      return "contact";
    case "privacy-policies":
      return "disclaimer";
    default:
      return page;
  }
};

export const renderStatusTag = (match) => {
  const { st, mins } = match;
  if (st === "Delayed") return <span className="status">Dly</span>;
  if (st === "Postponed") return <span className="sts_tg red">PPD</span>;
  if (["Abandoned", "Cancelled"].includes(st))
    return <span className="sts_tg red">AB</span>;
  if (st === "TF") return <span className="status red">TF</span>;
  if (st === "Finished" && mins === "To Finish")
    return <span className="status">To FT</span>;
  if (st === "Finished" && mins === "FAP")
    return <span className="status red">FAP</span>;
  if (st === "Finished" && mins === "FAET")
    return <span className="status">FAET</span>;
  if (st === "Finished") return <span className="sts_tg">Finished</span>;
  if (st === "NSY") return <span className="sts_tg">NSY</span>;
  return null;
};

export const typesMatchStatus = {
  notStartYet: "NYS",
  finished: "Finished",
  finishedAET: "Finished AET",
};

export const transformDataByRound = (inputData) => {
  const result = [];
  for (const round in inputData) {
    const roundData = inputData[round];
    let existingRound = result?.find((item) => item?.round === round);
    if (!existingRound) {
      existingRound = {
        d: round,
        matches: [],
      };
      result?.push(existingRound);
    }
    roundData?.forEach((match) => {
      existingRound?.matches?.push({
        ...match,
      });
    });
  }

  return { data: result };
};

export const addNewTab = (array, checkValue = "table", newItem = "teams") => {
  const index = array?.indexOf(checkValue);
  if (index !== -1) {
    array?.splice(index + 1, 0, newItem);
  }
  return array;
};

export const seoBaseOpenMatchDetailUrl = (sport, match, tab = "info") => {
  if (sport === "cricket") {
    switch (match?.bs) {
      case "live":
        tab = "live";
        break;
      case "Live":
        tab = "live";
        break;
      case "Cancelled":
        tab = "scorecard";
        break;
      case "Finished":
        tab = "scorecard";
        break;
      case "Scheduled":
        tab = "info";
        break;
      default:
        tab = "info";
        break;
    }
  }
  return tab;
};

export const getRemainingTimeInHoursMinutes = (dateString) => {
  const targetDate = new Date(dateString);
  targetDate?.setHours(targetDate?.getHours() + 5);
  const currentDate = new Date();

  const diffInMilliseconds = targetDate - currentDate;

  if (diffInMilliseconds <= 0) {
    return "00:00"; // If the target time has already passed, return 00:00
  }

  const hours = Math?.floor(diffInMilliseconds / (1000 * 60 * 60));
  const minutes = Math?.floor(
    (diffInMilliseconds % (1000 * 60 * 60)) / (1000 * 60)
  );

  // Format as HH:mm
  const formattedTime = `${String(hours)?.padStart(2, "0")}:${String(
    minutes
  )?.padStart(2, "0")}`;

  return formattedTime;
};


export function convertDateDMY(dateTimeString) {
  // Create a new Date object from the input string
  const date = new Date(dateTimeString);

  // Extract the year, month, and day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");

  // Format the date as YYYY-MM-DD
  return `${day}-${month}-${year}`;
}

export function getAgeString(birthDate) {
  if (!birthDate) return "-";
  const date = new Date(birthDate);
  if (isNaN(date)) return "-";

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}



//make url for links in livecenter

export const teamLeagueInfoUrl = ({ dataClient, websiteBaseUrl, category, dataPath, sport, name, id, tab = 'info' }) => {

  if (!dataClient) return "#";

  const basePath = websiteBaseUrl || "";
  const pathSegment = dataPath || sport;

  if (!name || !id) return "#";

  return `${basePath}/${pathSegment}/${category}/${linkName(name)}/${id}/${tab}`;
};

//make slug urls for filterdate

export const makeDateUrl = ({ websiteBaseUrl, dataPath, sport, slug = "" }) => {
  const base = websiteBaseUrl || "";
  const path = dataPath || sport.toLowerCase();
  return `${base}/${path}/${slug}`;
};

//allowed sports check

export const normalizeSport = (sport) => {
  const allowed = ["cricket", "football", "tennis", "basketball", "rugby", "golf"];
  return allowed.includes(sport.toLowerCase()) ? sport.toLowerCase() : "football";
};




export const buildTabsList = (sport, state, tabsData) => {
  if (sport === 'golf' && state === 'player') {
    return ['player_card']
  }
  else {
    const filteredTabs = (tabsData || []).filter(
      (tab) =>
        (sport === "golf" ? tab !== "summary" : tab !== "top_scorers" && tab !== "info")
    );

    const baseTabs = ["info", ...filteredTabs];

    if (sport === "football" && state === "league") {
      return addNewTab(baseTabs);
    }
    return baseTabs;
  }
};


export const getClassForResult = (result) => {
  if (result === "W") {
    return "clr_green";
  } else if (result === "L") {
    return "clr_red";
  }
  return "";
};

export const getTabsData = (
  dataClient,
  clientHeadData,
  headData,
  sourceData,
  sport,
  state
) => {
  if (sport === "golf" && state === "player") {
    return [];
  }

  return dataClient
    ? clientHeadData?.tabs || sourceData?.tabs
    : headData?.tabs || sourceData?.tabs;
};

export const getImageId = (state, sport, sourceData) => {
  if (state === "league" || state === "player") {
    return (
      sourceData?.football_category?.fid ||
      sourceData?.cric_category?.fid ||
      sourceData?.tennis_category?.fid ||
      sourceData?.basketball_category?.fid ||
      sourceData?.rugbyCategory?.fid ||
      sourceData?.icehockey_category?.fid ||
      sourceData?.category?.fid ||
      sourceData?.fid
    );
  }
  return sport === "tennis" ? sourceData?.pid : sourceData?.id;
};

export const getImageUrl = (baseURL, state, sport, imgId, path = 'v1', logo = "competitor-logos") => {
  let sports;
  switch (sport) {
    case "ice-hockey":
      sports = "hockey";
      break;
    case "rugby-union":
      sports = "rugby";
      break;
    case 'tennis':
      logo = 'player-pictures'
      break
    case 'golf':
      logo = 'player-pictures'
      break
    case "cricket":
      return `https://cric.livescoring.cc/teamflags/${imgId}.png`;
    default:
      sports = sport;
  }
  if (state === "league" || state === "player") {

    return `${baseURL}/public/assets/images/flags/country_${imgId}.png`;
  }


  return `${getImgUrl()}/${path}/sports/${sports}/${logo}/${imgId}.png`;
};


export const getMatchOutcomeClass = (teamId, match) => {
  const outcome = getMatchOutcome(teamId, match);
  if (outcome === "W") return "win";
  if (outcome === "L") return "loss";
  return "draw";
};

export const navigateToMatch = (match, dataClient, websiteBaseUrl, dataPath) => {
  if (dataClient) {
    window.location.href = websiteBaseUrl
      ? `${websiteBaseUrl}/${dataPath}/match-detail/${linkName(
        match?.competitors?.htn
      )}-vs-${linkName(match?.competitors?.atn)}/${match?.id}/info`
      : `/${dataPath}/match-detail/${linkName(
        match?.competitors?.htn
      )}-vs-${linkName(match?.competitors?.atn)}/${match?.id}/info`;
  }
};

export const navigateToLeague = (head, dataClient, websiteBaseUrl, dataPath) => {
  if (dataClient) {
    window.location.href = websiteBaseUrl
      ? `${websiteBaseUrl}/${dataPath}/league/${titleToSlug(
        head?.nm
      )}/${head?.id}/info`
      : `/${dataPath}/league/${titleToSlug(
        head?.nm
      )}/${head?.id}/info`;
  }
};


//Match Detail Helpers 
export const extractScores = (match, quarter) => {
  if (match) {
    const score = match[`q${quarter}`]?.split("-") || match[`p${quarter}`]?.split("-") || [];
    return {
      hScore: score[0] || 0,
      aScore: score[1] || 0,
    };
  }
  return null;
};

export const renderScores = (sport, scores, matchScore, isHome) => {
  if (sport === "icehockey" || sport === "basketball") {
    return scores?.map((score, i) => (
      <li key={score + i} className="clr_score">
        {isHome ? score?.hScore : score?.aScore}
      </li>
    ));
  }

  if (sport === "tennis") {
    const sets = isHome
      ? ["s1home", "s2home", "s3home", "s4home", "s5home"]
      : ["s1away", "s2away", "s3away", "s4away", "s5away"];

    return sets.map((set, idx) => {
      const score = matchScore[set];
      const tbScore = matchScore[`s${idx + 1}${isHome ? "hometb" : "awaytb"}`];
      return (
        score && (
          <span key={set} className="score">
            {score}
            <sup>{tbScore}</sup>
          </span>
        )
      );
    });
  }

  return null;
};

export const getMatchImageUrl = (sport, competitorId) =>
  getImageUrl(
    "", '', sport, competitorId
  )

// Function to handle updating the match based on the sport type
export const updateMatches = (m, tempMatch, sport) => {
  switch (sport) {
    case "basketball":
      return {
        ...m,
        st: tempMatch?.st ?? m.st,
        cp: tempMatch?.cp ?? m.cp,
        ft: tempMatch?.ft ?? m.ft,
        q1: tempMatch?.q1 ?? m.q1,
        q2: tempMatch?.q2 ?? m.q2,
        q3: tempMatch?.q3 ?? m.q3,
        q4: tempMatch?.q4 ?? m.q4,
        odds: tempMatch?.odds ?? m.odds,
        mins: tempMatch?.mins ?? m.mins,
        fot: tempMatch?.fot ?? m.fot,
        cs: tempMatch?.cs ?? m.cs,
        ot: tempMatch?.ot ?? m.ot,
        cfs: tempMatch?.cfs ?? m.cfs,
      };
    case "icehockey":
      return {
        ...m,
        st: tempMatch?.st ?? m.st,
        cp: tempMatch?.cp ?? m.cp,
        ft: tempMatch?.ft ?? m.ft,
        p1: tempMatch?.p1 ?? m.p1,
        p2: tempMatch?.p2 ?? m.p2,
        p3: tempMatch?.p3 ?? m.p3,
        odds: tempMatch?.odds ?? m.odds,
        mins: tempMatch?.mins ?? m.mins,
        fot: tempMatch?.fot ?? m.fot,
        cs: tempMatch?.cs ?? m.cs,
        ot: tempMatch?.ot ?? m.ot,
        cfs: tempMatch?.cfs ?? m.cfs,
      };
    case "tennis":
      return {
        ...m,
        st: tempMatch?.st ?? m.st,
        cp: tempMatch?.cp ?? m.cp,
        scores: tempMatch?.scores ?? m.scores,
        odds: tempMatch?.odds ?? m.odds,
        fts: tempMatch?.fts ?? m.fts,
      };
    case "cricket":
      return {
        ...m,
        st: tempMatch?.st ?? m.st,
        bs: tempMatch?.bs ?? m.bs,
        hts: tempMatch?.hts ?? m.hts,
        ats: tempMatch?.ats ?? m.ats,
        win: tempMatch?.win ?? m.win,
        batid: tempMatch?.batid ?? m.batid,
        botid: tempMatch?.botid ?? m.botid,
        odds: tempMatch?.odds ?? m.odds,
        com: tempMatch?.com ?? m.com,
        rr: tempMatch?.rr ?? m.rr,
      };
    default:
      return m; // If no sport matches, return the original match object
  }
};



export const Icon = ({ type, src, width, height, alt }) => (
  <span className={type}>
    <NextImage src={src} width={width} height={height} alt={alt} />
  </span>
);


// utils/matchUtils.js


export const calculateWinner = (scores) => {
  let totalHomeScore = 0;
  let totalAwayScore = 0;

  scores.forEach((s) => {
    totalHomeScore += parseInt(s.hScore) || 0;
    totalAwayScore += parseInt(s.aScore) || 0;
  });

  if (totalHomeScore > totalAwayScore) return "home";
  if (totalAwayScore > totalHomeScore) return "away";
  return "draw";
};

export const getTeamLogos = (sport, match) => {
  if (sport === "tennis") {
    return {
      home: getImageUrl('', '', sport, match?.competitors?.hpid, 'player-pictures'),
      away: getImageUrl('', '', sport, match?.competitors?.apid, 'player-pictures'),
    };
  } else if (sport === "cricket") {
    return {
      home: `https://cric.livescoring.cc/teamflags/${match?.competitors?.htid}.png`,
      away: `https://cric.livescoring.cc/teamflags/${match?.competitors?.atid}.png`,
    };
  }
  return {
    home: getImageUrl('', '', sport, match?.competitors?.htid),
    away: getImageUrl('', '', sport, match?.competitors?.atid),
  };
};

export const getPredictionWidth = (t1P, t2P, drawLable, drawP) => {
  let t1W = "95%";
  let t2W = "95%";
  let drawW = "33.33%";

  if (drawLable) {
    // When showing draws, calculate proportional widths for all three
    // Ensure we have valid numeric values
    const t1Num = typeof t1P === 'number' ? t1P : parseFloat(String(t1P)) || 0;
    const t2Num = typeof t2P === 'number' ? t2P : parseFloat(String(t2P)) || 0;
    const drawNum = typeof drawP === 'number' ? drawP : parseFloat(String(drawP)) || 0;
    const total = t1Num + t2Num + drawNum;

    if (total === 0) {
      // All are 0, show equal bars
      t1W = "33.33%";
      t2W = "33.33%";
      drawW = "33.34%";
    } else {
      // Calculate proportional widths based on percentage values
      const t1Proportion = (t1Num / total) * 100;
      const t2Proportion = (t2Num / total) * 100;
      const drawProportion = (drawNum / total) * 100;

      // Ensure minimum 10% visibility for ALL values (including 0%)
      const minWidth = 10;
      let adjustedT1 = Math.max(minWidth, t1Proportion);
      let adjustedT2 = Math.max(minWidth, t2Proportion);
      let adjustedDraw = Math.max(minWidth, drawProportion);

      // Normalize to ensure they add up to 100%
      const adjustedTotal = adjustedT1 + adjustedT2 + adjustedDraw;
      if (adjustedTotal > 0) {
        // Round to 1 decimal to ensure consistent formatting
        const t1Final = ((adjustedT1 / adjustedTotal) * 100).toFixed(1);
        const t2Final = ((adjustedT2 / adjustedTotal) * 100).toFixed(1);
        const drawFinal = ((adjustedDraw / adjustedTotal) * 100).toFixed(1);

        t1W = `${t1Final}%`;
        t2W = `${t2Final}%`;
        drawW = `${drawFinal}%`;
      } else {
        t1W = "33.33%";
        t2W = "33.33%";
        drawW = "33.34%";
      }
    }
  } else {
    // Convert to numbers for comparison
    const t1Num = parseFloat(t1P) || 0;
    const t2Num = parseFloat(t2P) || 0;
    const total = t1Num + t2Num;

    if (total === 0) {
      // Both are 0, show equal minimal bars
      t1W = "50%";
      t2W = "50%";
    } else if (t1Num === 0 && t2Num > 0) {
      // Only t2 has value
      t1W = "5%";
      t2W = "95%";
    } else if (t2Num === 0 && t1Num > 0) {
      // Only t1 has value
      t1W = "95%";
      t2W = "5%";
    } else if (t1Num === t2Num) {
      // Equal percentages, show equal bars
      t1W = "50%";
      t2W = "50%";
    } else {
      // Calculate proportional widths based on percentage values
      // Calculate each team's proportion of the total
      const t1Proportion = (t1Num / total) * 100;
      const t2Proportion = (t2Num / total) * 100;

      // Ensure minimum 10% visibility for smaller value
      const minWidth = 10;

      if (t1Proportion < minWidth) {
        t1W = `${minWidth}%`;
        t2W = `${100 - minWidth}%`;
      } else if (t2Proportion < minWidth) {
        t2W = `${minWidth}%`;
        t1W = `${100 - minWidth}%`;
      } else {
        // Both are above minimum, use proportional widths
        t1W = `${t1Proportion.toFixed(1)}%`;
        t2W = `${t2Proportion.toFixed(1)}%`;
      }
    }
  }

  return { t1W, t2W, drawW };
};

export const calculateFormScore = (formArray) => {
  const result = { score: 0, wins: 0, draws: 0, losses: 0 };

  if (!formArray || !Array.isArray(formArray)) return result;

  formArray.forEach((item) => {
    if (item === 'W') {
      result.score += 3;
      result.wins += 1;
    } else if (item === 'D') {
      result.score += 1;
      result.draws += 1;
    } else if (item === 'L') {
      result.losses += 1;
    }
  });

  return result;
};

/**
 * Sorts teams by their recent form performance
 * @param {array} data - Array of team standings data
 * @returns {array} - Sorted array by form score
 */
export const sortByForm = (data) => {
  if (!data || !Array.isArray(data)) return [];

  return [...data].sort((a, b) => {
    const formA = a?.form ? JSON.parse(a.form) : [];
    const formB = b?.form ? JSON.parse(b.form) : [];

    const scoreA = calculateFormScore(formA);
    const scoreB = calculateFormScore(formB);

    // Primary sort: by total form score (descending)
    if (scoreB.score !== scoreA.score) {
      return scoreB.score - scoreA.score;
    }

    // Secondary sort: by number of wins (descending)
    if (scoreB.wins !== scoreA.wins) {
      return scoreB.wins - scoreA.wins;
    }

    // Tertiary sort: by fewer losses (ascending)
    return scoreA.losses - scoreB.losses;
  });
};

export const sortFootballMatchesByDate = (tournaments, ascending = false) => {
  if (!tournaments || tournaments?.length === 0) return null;
  const allMatches = [];
  const result = [];
  let currentTournamentId = null;

  tournaments?.forEach((tournament) => {
    if (tournament?.matches && tournament?.matches.length > 0) {
      tournament?.matches?.forEach((match) => {
        allMatches.push({
          match: match,
          tournament: tournament,
        });
      });
    }
  });

  allMatches?.sort((a, b) => {
    const dateA = new Date(a?.match?.dt);
    const dateB = new Date(b?.match?.dt);

    return ascending ? dateA - dateB : dateB - dateA;
  });

  allMatches?.forEach(({ match, tournament }) => {
    if (currentTournamentId !== tournament?.id) {
      result?.push({
        ...tournament,
        matches: [match],
      });
      currentTournamentId = tournament?.id;
    } else {
      result[result?.length - 1]?.matches.push(match);
    }
  });

  return { data: result };
};
